(() => {
  'use strict';
  if (customElements.get('rv-vehicle-fit')) return;
  const assets = new Map();
  const modelCache = new Map();
  const normal = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const title = value => ({BMW:'BMW', GMC:'GMC', MINI:'MINI', RAM:'Ram', 'MERCEDES-BENZ':'Mercedes-Benz', 'LAND ROVER':'Land Rover', 'ALFA ROMEO':'Alfa Romeo', 'ROLLS-ROYCE':'Rolls-Royce', 'ISUZU':'Isuzu'}[value] || value.toLowerCase().replace(/(^|[\s-])\w/g, letter => letter.toUpperCase()));
  const popular = ['TOYOTA','FORD','HONDA','CHEVROLET','BMW','AUDI','MERCEDES-BENZ','TESLA','NISSAN','HYUNDAI','KIA','SUBARU','VOLKSWAGEN','CHRYSLER','DODGE','JEEP'];
  const founderFits = new Set(['2024|audi|a5sportback','2013|bmw|m3','2025|audi|rs6','2026|ford|maverick','1967|ford|fairlane']);

  async function getJSON(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 7000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
      if (!response.ok) throw new Error('Vehicle catalog unavailable');
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  function asset(url) {
    if (!assets.has(url)) assets.set(url, getJSON(url).catch(error => { assets.delete(url); throw error; }));
    return assets.get(url);
  }
  async function modelsFor(make, seedURL) {
    const key = String(make.id);
    if (modelCache.has(key)) return modelCache.get(key);
    const pending = (async () => {
      const seed = await asset(seedURL).catch(() => null);
      const stored = seed?.entries?.[key];
      if (stored) return stored.models;
      const types = [...new Set(make.vehicleTypes.map(type => Number(type.id)))].filter(id => [2,3,5,7].includes(id));
      const slugs = {2:'car',3:'truck',5:'bus',7:'multipurpose passenger vehicle'};
      const results = [];
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(2, types.length) }, async () => {
        while (next < types.length) {
          const type = types[next++];
          const url = `https://vpic.nhtsa.dot.gov/api/vehicles/GetModelsForMakeIdYear/makeId/${encodeURIComponent(key)}/vehicletype/${encodeURIComponent(slugs[type])}?format=json`;
          const response = await getJSON(url);
          if (!Array.isArray(response.Results)) throw new Error('Invalid vehicle catalog');
          results.push(...response.Results);
        }
      }));
      const unique = new Map();
      results.forEach(row => {
        if (row.Model_Name && row.Model_ID) unique.set(String(row.Model_ID), {id:row.Model_ID,name:row.Model_Name});
      });
      return [...unique.values()].sort((a,b) => a.name.localeCompare(b.name, undefined, {numeric:true}));
    })();
    modelCache.set(key, pending);
    try { return await pending; } catch (error) { modelCache.delete(key); throw error; }
  }

  class FitFinder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      queueMicrotask(() => this.init());
    }
    init() {
      if (!this.isConnected || this.ready) return;
      this.ready = true;
      this.make = this.querySelector('[data-fit-make]');
      this.model = this.querySelector('[data-fit-model]');
      this.year = this.querySelector('[data-fit-year]');
      this.result = this.querySelector('[data-fit-result]');
      this.status = this.querySelector('[data-fit-status]');
      this.dialog = this.querySelector('dialog');
      this.form = document.getElementById(this.dataset.formId);
      this.buy = this.querySelector('[data-fit-buy]');
      this.strings = JSON.parse(this.querySelector('[data-fit-strings]')?.textContent || '{}');
      this.makes = []; this.models = []; this.request = 0;
      const on = (el,type,handler) => el?.addEventListener(type,handler,{signal:this.controller.signal});
      while(this.year.options.length>1)this.year.remove(1);
      for (let year = new Date().getFullYear() + 1; year >= 1900; year--) {
        const option = document.createElement('option'); option.value = year; option.textContent = year; this.year.append(option);
      }
      this.combos = [this.combo(this.make,this.querySelector('[data-fit-makes]'),'make'),this.combo(this.model,this.querySelector('[data-fit-models]'),'model')];
      on(this.querySelector('[data-fit-open]'),'click',event => {
        if (!this.dialog?.showModal) return;
        event.preventDefault(); this.dialog.showModal(); this.open();
      });
      on(this.querySelector('[data-fit-close]'),'click',() => this.dialog.close());
      on(this.dialog,'click',event => { if(event.target === this.dialog) {
        const box=this.dialog.getBoundingClientRect();
        if(event.clientX<box.left||event.clientX>box.right||event.clientY<box.top||event.clientY>box.bottom)this.dialog.close();
      }});
      on(this.dialog,'close',() => { this.request++;this.combos.forEach(combo => combo.close());this.model.disabled=false;this.say(''); });
      on(this.querySelector('[data-fit-manual]'),'click',() => this.manual());
      on(this.querySelector('[data-fit-check]'),'click',() => this.showResult());
      on(this.querySelector('[data-fit-change]'),'click',() => {this.classList.remove('has-result');this.result.hidden=true;this.querySelector('[data-fit-check]').hidden=false;this.make.focus({preventScroll:true});});
      on(this.querySelector('[data-fit-skip]'),'click',() => this.showResult(true));
      on(this.year,'change',() => { if(!this.result.hidden)this.showResult(this.skipped); });
      on(this.form,'input',() => queueMicrotask(() => this.syncBuy()));
      on(this.form,'change',() => queueMicrotask(() => this.syncBuy()));
      on(this.form,'submit',() => queueMicrotask(() => { this.buy.disabled=true; this.buy.setAttribute('aria-busy','true'); }));
      on(window,'pageshow',() => { this.buy?.removeAttribute('aria-busy');this.syncBuy(); });
      on(this,'fit:activate',() => this.open());
      this.syncBuy();
      if(this.classList.contains('rv-vehicle-fit--inline') && !this.closest('[hidden]')) this.open(false);
    }
    say(message) { this.status.textContent=message || ''; }
    async open(focus=true) {
      this.syncBuy();
      if(focus)this.make.focus({preventScroll:true});
      if(this.makes.length || this.loading)return;
      this.loading=true;this.say(this.strings.loadingMakes);
      try {
        const data=await asset(this.dataset.makesUrl);
        if(!Array.isArray(data.makes))throw new Error('Invalid catalog');
        this.makes=data.makes.filter(row=>row.id&&row.name).map(row=>({...row,label:title(row.name)}));
        this.say('');
        if(document.activeElement===this.make&&!this.manualMode&&!this.skipped&&(!this.dialog||this.dialog.open))this.combos[0].render();
      }catch(error){this.say(this.strings.catalogFallback);this.querySelector('[data-fit-check]').hidden=false;this.manualMode=true;this.model.disabled=false;}
      finally{this.loading=false;}
    }
    combo(input,list,kind) {
      let matches=[],active=-1;
      const close=()=>{list.hidden=true;input.setAttribute('aria-expanded','false');input.removeAttribute('aria-activedescendant');active=-1;};
      const select=row=>{
        input.value=row.label || row.name;close();
        if(kind==='make')this.selectMake(row);else{this.chosenModel=row;this.showResult();}
      };
      const render=()=>{
        if(this.manualMode)return close();
        const query=normal(input.value);
        const rows=kind==='make'?this.makes:this.models;
        const sorted=rows.filter(row=>normal(row.label||row.name).includes(query)).sort((a,b)=>{
          if(query)return Number(normal(b.label||b.name).startsWith(query))-Number(normal(a.label||a.name).startsWith(query));
          if(kind==='make'){const ai=popular.indexOf(a.name),bi=popular.indexOf(b.name);return(ai<0?999:ai)-(bi<0?999:bi)||a.name.localeCompare(b.name);}
          return 0;
        });
        matches=sorted.slice(0,60);list.replaceChildren();active=-1;
        matches.forEach((row,index)=>{
          const li=document.createElement('li');li.id=`${input.id}-option-${index}`;li.setAttribute('role','option');li.setAttribute('aria-selected','false');li.textContent=row.label||row.name;
          li.addEventListener('pointerdown',event=>event.preventDefault());li.addEventListener('click',()=>select(row));list.append(li);
        });
        if(!matches.length){const li=document.createElement('li');li.setAttribute('role','option');li.setAttribute('aria-disabled','true');li.textContent=this.strings.noMatches;list.append(li);}
        list.hidden=false;input.setAttribute('aria-expanded','true');input.removeAttribute('aria-activedescendant');
      };
      input.addEventListener('focus',render,{signal:this.controller.signal});
      input.addEventListener('input',()=>{
        this.result.hidden=true;this.classList.remove('has-result');this.skipped=false;this.say('');
        if(kind==='make'){this.chosenMake=null;this.chosenModel=null;this.model.value='';this.models=[];this.model.disabled=!this.manualMode;this.request++;this.year.value='';}
        else this.chosenModel=null;
        render();
      },{signal:this.controller.signal});
      input.addEventListener('blur',()=>setTimeout(close,180),{signal:this.controller.signal});
      input.addEventListener('keydown',event=>{
        if(event.key==='Escape'&&!list.hidden){event.preventDefault();event.stopPropagation();close();return;}
        if(event.key==='Tab'){close();return;}
        if(event.key==='ArrowDown'||event.key==='ArrowUp'){
          event.preventDefault();if(list.hidden)render();if(!matches.length)return;
          active=active<0?(event.key==='ArrowDown'?0:matches.length-1):(active+(event.key==='ArrowDown'?1:-1)+matches.length)%matches.length;
          [...list.children].forEach((li,index)=>li.setAttribute('aria-selected',String(index===active)));
          input.setAttribute('aria-activedescendant',list.children[active].id);list.children[active].scrollIntoView({block:'nearest'});
        }else if(event.key==='Enter'){
          event.preventDefault();if(!list.hidden&&matches.length)select(matches[Math.max(0,active)]);else if(this.manualMode)this.showResult();
        }
      },{signal:this.controller.signal});
      return{close,render};
    }
    async selectMake(make) {
      const request=++this.request;
      this.chosenMake=make;this.chosenModel=null;this.model.value='';this.year.value='';this.models=[];this.model.disabled=true;this.result.hidden=true;this.manualMode=false;
      this.say(this.strings.loadingModels);
      try{
        const rows=await modelsFor(make,this.dataset.modelsUrl);
        if(request!==this.request||!this.isConnected)return;
        this.models=rows.map(row=>({id:row.id,name:row.name}));this.model.disabled=false;this.say(rows.length?'':this.strings.modelFallback);
        if(!rows.length)this.querySelector('[data-fit-check]').hidden=false;
        this.model.focus({preventScroll:true});this.combos[1].render();
      }catch(error){if(request!==this.request)return;this.model.disabled=false;this.say(this.strings.modelFallback);this.manualMode=true;this.querySelector('[data-fit-check]').hidden=false;this.model.focus({preventScroll:true});}
    }
    manual() {
      this.request++;this.manualMode=true;this.model.disabled=false;this.combos.forEach(combo=>combo.close());
      this.querySelector('[data-fit-check]').hidden=false;this.say(this.strings.manualHint);
      if(this.make.value)this.model.focus({preventScroll:true});else this.make.focus({preventScroll:true});
    }
    showResult(skip=false) {
      if(!skip&&(!this.make.value.trim()||!this.model.value.trim())){this.say(this.strings.enterVehicle);(!this.make.value.trim()?this.make:this.model).focus();return;}
      if(skip){this.request++;this.model.disabled=false;}
      this.skipped=skip;this.combos.forEach(combo=>combo.close());this.say('');
      const vehicle=skip?'':`${this.year.value?this.year.value+' ':''}${this.make.value.trim().slice(0,80)} ${this.model.value.trim().slice(0,80)}`;
      const known=!skip&&this.dataset.productKind==='door'&&founderFits.has(`${this.year.value}|${normal(this.make.value)}|${normal(this.model.value)}`);
      this.querySelector('[data-fit-vehicle]').textContent=vehicle||this.strings.yourCar;
      this.querySelector('[data-fit-badge]').textContent=known?this.strings.founderConfirmed:this.strings.universalDesign;
      this.querySelector('[data-fit-heading]').textContent=known?this.strings.knownHeading:(this.dataset.productKind==='door'?this.strings.doorHeading:this.dataset.productKind==='trunk'?this.strings.trunkHeading:this.strings.pairHeading);
      this.result.hidden=false;this.classList.add('has-result');if(this.dialog)this.dialog.scrollTop=0;this.result.dataset.confirmation=known?'founder-confirmed':'universal-design';this.syncBuy();this.querySelector('[data-fit-heading]').focus({preventScroll:true});
    }
    syncBuy() {
      if(!this.buy)return;
      const quantity=Number(this.form?.querySelector('[name="quantity"]')?.value||1);
      const source=this.form?.querySelector('[data-purchase]');
      const valid=Number.isSafeInteger(quantity)&&quantity>0;
      this.buy.disabled=!valid||this.dataset.available!=='true'||Boolean(source?.disabled)||!this.form;
      const label=this.querySelector('[data-fit-buy-label]');
      const count=this.strings.addCount?.replace('__QUANTITY__',String(quantity)).replace('__PRODUCT__',this.dataset.productLabel);
      label.textContent=this.dataset.available!=='true'?this.strings.soldOut:(quantity===1?this.strings.addSingle:count);
      const bundle=this.form?.querySelector('rv-door-bundles');
      let cents=Number(this.dataset.unitPrice)*quantity;
      if(bundle&&!bundle.hidden&&this.dataset.currency==='USD'&&Number(this.dataset.unitPrice)===9900)cents-=quantity>=3?3800:quantity===2?1900:0;
      if(valid&&Number.isFinite(cents))this.querySelector('[data-fit-price]').textContent=new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:this.dataset.currency}).format(cents/100);
    }
    disconnectedCallback(){this.controller?.abort();this.controller=null;this.ready=false;this.request++;}
  }
  customElements.define('rv-vehicle-fit',FitFinder);
  document.addEventListener('click',event=>{
    const button=event.target.closest('[data-fit-product-tab]');if(!button)return;
    const page=button.closest('[data-fit-page]');
    page.querySelectorAll('[data-fit-product-tab]').forEach(tab=>tab.setAttribute('aria-pressed',String(tab===button)));
    page.querySelectorAll('[data-fit-product-panel]').forEach(panel=>{panel.hidden=panel.dataset.fitProductPanel!==button.dataset.fitProductTab;});
    page.querySelector(`[data-fit-product-panel="${button.dataset.fitProductTab}"] rv-vehicle-fit`)?.dispatchEvent(new Event('fit:activate'));
  });
  const chooseInitial=()=>{
    const product=new URLSearchParams(location.search).get('product');
    if(['door','trunk','pair'].includes(product))document.querySelector(`[data-fit-product-tab="${product}"]`)?.click();
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',chooseInitial,{once:true});else chooseInitial();
})();
